import { useState, type FormEvent } from "react";
import type {
  PortalWording,
  ProviderId,
  SignInNotice,
  SignInScreenState,
  WayId,
} from "@shared-core/domain/portal-sign-in/types";
import { Card } from "../primitives/card";
import { Input } from "../primitives/input";
import { Label } from "../primitives/label";
import { Notice, SignInFrame } from "./SignInFrame";
import { WayButton } from "./WayButton";

const PROVIDER_NAMES: Record<ProviderId, string> = {
  microsoft: "Microsoft",
  google: "Google",
};

export interface SignInScreenProps {
  state: SignInScreenState;
  wording: PortalWording;
  busy?: boolean;
  onProvider(provider: ProviderId): void;
  onPasskey(): void;
  /** An address typed on the cold screen, or the known address on the others. */
  onEmailCode(address: string): void;
  onDifferentAddress(): void;
  onSignInAs(address: string): void;
}

function NoticeText({
  notice,
  wording,
  onSignInAs,
}: {
  notice: SignInNotice;
  wording: PortalWording;
  onSignInAs(address: string): void;
}) {
  switch (notice.kind) {
    case "different_account": {
      const provider = PROVIDER_NAMES[notice.provider];
      return (
        <Notice tone="warn">
          {provider} signed you in as <b>{notice.signedInAs}</b>, but this{" "}
          {notice.forInvite ? "invite is for" : "link was sent to"}{" "}
          <b>{notice.expectedAddress}</b>.
          {notice.offerSignInAs ? (
            <>
              {" "}
              <button
                type="button"
                className="font-medium underline underline-offset-2"
                onClick={() => onSignInAs(notice.signedInAs)}
              >
                Sign in as {notice.signedInAs} instead
              </button>
            </>
          ) : null}
        </Notice>
      );
    }
    case "replaces_session":
      return (
        <p className="text-sm text-muted-foreground">
          Signing in will sign {notice.signedInAddress} out of this browser.
        </p>
      );
    case "provider_not_approved":
      return (
        <Notice tone="warn">
          Your organisation&apos;s IT hasn&apos;t approved the {wording.appName}{" "}
          for Microsoft sign-in yet. Use an emailed code instead, or ask IT to
          approve &ldquo;{wording.appName}&rdquo;.
        </Notice>
      );
  }
}

/**
 * Sign in, in its three shapes: cold (the address unknown), known (an address a link or a lapsed session names) and
 * create your account (an invite's address, to be confirmed). The server decides which and which ways are offered;
 * this draws them.
 */
export function SignInScreen({
  state,
  wording,
  busy = false,
  onProvider,
  onPasskey,
  onEmailCode,
  onDifferentAddress,
  onSignInAs,
}: SignInScreenProps) {
  const [typedAddress, setTypedAddress] = useState("");
  const isCold = state.mode === "cold";
  const isCreate = state.mode === "create";
  const address = state.address ?? "";

  function submitTyped(event: FormEvent) {
    event.preventDefault();
    if (typedAddress.trim()) onEmailCode(typedAddress.trim());
  }

  const notice = state.notice ? (
    <NoticeText
      notice={state.notice}
      wording={wording}
      onSignInAs={onSignInAs}
    />
  ) : null;

  const buttonWays = state.ways.filter((way) => way !== "code");
  const hasCode = state.ways.includes("code");

  function wayButton(way: WayId) {
    const isLastUsed = state.lastUsed === way;
    if (way === "passkey") {
      return (
        <WayButton
          key={way}
          way={way}
          isLastUsed={isLastUsed}
          disabled={busy}
          onClick={onPasskey}
        >
          Use a passkey
        </WayButton>
      );
    }
    return (
      <WayButton
        key={way}
        way={way}
        isLastUsed={isLastUsed}
        disabled={busy}
        onClick={() => onProvider(way as ProviderId)}
      >
        {isCreate ? "Confirm with" : "Continue with"}{" "}
        {PROVIDER_NAMES[way as ProviderId]}
      </WayButton>
    );
  }

  if (isCold) {
    return (
      <SignInFrame
        eyebrow={wording.brand}
        title={wording.tagline}
        subtitle="Sign in with the account you use for work."
        footer="New here? Use the link in your invite email, or sign in to ask for access."
      >
        {notice}
        <Card className="gap-3 p-5">
          {buttonWays.map(wayButton)}
          {hasCode ? (
            <>
              {buttonWays.length ? (
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="h-px flex-1 bg-border" />
                  or
                  <span className="h-px flex-1 bg-border" />
                </div>
              ) : null}
              <form className="space-y-3" onSubmit={submitTyped}>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="portal-sign-in-address"
                    className="font-normal"
                  >
                    Email address
                  </Label>
                  <Input
                    id="portal-sign-in-address"
                    type="email"
                    autoComplete="username webauthn"
                    placeholder="you@yourcompany.co.uk"
                    value={typedAddress}
                    onChange={(event) => setTypedAddress(event.target.value)}
                  />
                </div>
                <WayButton
                  way="code"
                  type="submit"
                  isLastUsed={state.lastUsed === "code"}
                  disabled={busy}
                >
                  Email me a code
                </WayButton>
              </form>
            </>
          ) : null}
        </Card>
      </SignInFrame>
    );
  }

  const headline = isCreate
    ? "Create your account"
    : state.recordLabel
      ? `Sign in to see ${state.recordLabel}`
      : "Sign in";
  const subtitle = isCreate ? (
    <>
      {wording.company} has invited <b>{address}</b> to the portal for{" "}
      {state.partyName}. Confirm that the address is yours to create your
      account.
    </>
  ) : (
    <>
      Sign in as <b>{address}</b>.
    </>
  );

  return (
    <SignInFrame
      eyebrow={state.partyName ?? wording.brand}
      title={headline}
      subtitle={subtitle}
      footer={
        isCreate ? (
          "There's nothing else to fill in. You can add a passkey afterwards."
        ) : (
          <button
            type="button"
            className="text-sm font-medium text-primary underline underline-offset-2 disabled:opacity-50"
            disabled={busy}
            onClick={onDifferentAddress}
          >
            Sign in with a different address
          </button>
        )
      }
    >
      {state.notice?.kind === "replaces_session" ? null : notice}
      <Card className="gap-3 p-5">
        {state.notice?.kind === "replaces_session" ? notice : null}
        {buttonWays.map(wayButton)}
        {hasCode ? (
          <WayButton
            way="code"
            isLastUsed={state.lastUsed === "code"}
            disabled={busy}
            onClick={() => onEmailCode(address)}
          >
            Email a code to {address}
          </WayButton>
        ) : null}
      </Card>
    </SignInFrame>
  );
}
